import { Fragment, useEffect, useState } from "react";
import {
  Alert,
  AlertDescription,
  AlertIcon,
  AlertTitle,
  Badge,
  Box,
  Button,
  Checkbox,
  FormControl,
  FormLabel,
  Heading,
  HStack,
  IconButton,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  Spinner,
  Stat,
  StatGroup,
  StatLabel,
  StatNumber,
  Table,
  Tbody,
  Td,
  Text,
  Th,
  Thead,
  Tr,
  Textarea,
  useDisclosure,
  useToast,
} from "@chakra-ui/react";
import { ChevronDownIcon, ChevronRightIcon } from "@chakra-ui/icons";
import PlacingStatusBadge from "./PlacingStatusBadge";
import { usePartyNames } from "../../hooks/usePartyNames";
import { getQuoteRequestsForPlacement, nominateLeads } from "../../services/PlacingAPI";
import { showApiErrorToast } from "../../utils/showApiToast";
import { RESPONSES, formatDate, formatShareRange, readParticipation } from "./placingUtils";
import { ReasonSelect, ReasonText } from "./ReasonFields";
import { useReasonCodes } from "../../hooks/useReasonCodes";

// A quote can be nominated while its request is quoted and the quote is
// still live. Comparison rows don't always carry a quote status; when they
// do it must be "sent".
const canNominate = (row, quote) =>
  row.status === "quoted" && !quote.expired && (quote.status == null || quote.status === "sent");

/** Broker: compare quotes side by side and nominate the lead(s) (steps 11 and 12). */
const QuoteComparison = ({ submissionId, placementId, comparison, onChanged, leads = [], isClosed = false }) => {
  // Each carrier's build response (declare interest / decline), from its submission entry
  const leadsByParty = Object.fromEntries(leads.map((l) => [l.partyId, l]));
  const [openDetail, setOpenDetail] = useState({}); // quoteId -> shown
  const toast = useToast();
  const [expanded, setExpanded] = useState({});
  const [chosen, setChosen] = useState({}); // partyId -> quoteId
  const [nominating, setNominating] = useState(false);
  const [result, setResult] = useState(null);
  const confirmModal = useDisclosure();
  const [ntuReasonCode, setNtuReasonCode] = useState("");
  const [ntuNotes, setNtuNotes] = useState("");
  const { isOther } = useReasonCodes();

  const rows = comparison?.rows || [];
  const names = usePartyNames(rows.map((r) => r.partyId));
  // Nominating happens once, and not at all once the risk is signed down
  const alreadyNominated = isClosed || rows.some((r) => r.status === "nominated");

  // The quote requests themselves, for each row's sent date, notes and terms
  // (a carrier can have several, so the broker needs to tell them apart)
  const [requestsById, setRequestsById] = useState({});
  useEffect(() => {
    if (!placementId) return;
    getQuoteRequestsForPlacement(placementId)
      .then((list) => setRequestsById(Object.fromEntries(list.map((q) => [q._id, q]))))
      .catch(() => setRequestsById({}));
  }, [placementId, comparison]);

  if (!comparison) return <Spinner />;

  // One group per carrier, in the order the rows came, each with its requests
  const groups = rows.reduce((list, row) => {
    const group = list.find((g) => g.partyId === row.partyId);
    if (group) group.rows.push(row);
    else list.push({ partyId: row.partyId, rows: [row] });
    return list;
  }, []);

  const toggleRow = (id) => setExpanded((e) => ({ ...e, [id]: !e[id] }));
  // At most one quote per carrier (ONE_QUOTE_PER_LEAD)
  const chooseQuote = (partyId, quoteId) =>
    setChosen((c) => {
      const next = { ...c };
      if (next[partyId] === quoteId) delete next[partyId];
      else next[partyId] = quoteId;
      return next;
    });

  // What nominating will NTU: every other live quote and open quote request
  const chosenQuoteIds = Object.values(chosen);
  const chosenRequestIds = rows.filter((r) => (r.quotes || []).some((q) => chosenQuoteIds.includes(q.quoteId))).map((r) => r.quoteRequestId);
  const quotesToNtu = rows
    .flatMap((r) => r.quotes || [])
    .filter((q) => !chosenQuoteIds.includes(q.quoteId) && (q.status == null || q.status === "sent")).length;
  const requestsToNtu = rows.filter((r) => ["sent", "quoted"].includes(r.status) && !chosenRequestIds.includes(r.quoteRequestId)).length;
  const ntuNeeded = quotesToNtu + requestsToNtu > 0;
  const leadNames = Object.keys(chosen).map((id) => names[id] || "the chosen carrier").join(", ");

  const nominate = async () => {
    if (ntuNeeded && !ntuReasonCode) return toast({ title: "Choose a reason for NTU", status: "warning" });
    if (ntuNeeded && isOther(ntuReasonCode) && !ntuNotes.trim()) {
      return toast({ title: "Add notes for the NTU (you chose other)", status: "warning" });
    }
    setNominating(true);
    try {
      setResult(
        await nominateLeads(submissionId, chosenQuoteIds, ntuNeeded ? { ntuReasonCode, ntuNotes: ntuNotes.trim() } : {})
      );
      setChosen({});
      confirmModal.onClose();
      toast({ title: "Lead(s) nominated", status: "success" });
      await onChanged();
    } catch (err) {
      showApiErrorToast(toast, err, { title: "Couldn't nominate leads" });
    } finally {
      setNominating(false);
    }
  };

  return (
    <Box borderWidth="1px" borderRadius="md" p={4} bg="whiteAlpha.800">
      <Heading size="sm" mb={3}>Quotes</Heading>

      <StatGroup mb={4}>
        <Stat><StatLabel>Quote requests</StatLabel><StatNumber>{comparison.quoteRequests ?? rows.length}</StatNumber></Stat>
        <Stat><StatLabel>Quoted</StatLabel><StatNumber color="purple.600">{comparison.quoted ?? 0}</StatNumber></Stat>
        <Stat><StatLabel>Declined</StatLabel><StatNumber color="red.600">{comparison.declined ?? 0}</StatNumber></Stat>
        <Stat><StatLabel>Awaiting</StatLabel><StatNumber color="blue.600">{comparison.awaiting ?? 0}</StatNumber></Stat>
      </StatGroup>

      {result && (
        <Alert status="success" mb={4} alignItems="flex-start">
          <AlertIcon />
          <Box>
            <AlertTitle>Nominated</AlertTitle>
            <AlertDescription fontSize="sm">
              {(result.nominated || []).length} lead quote(s) nominated; {result.quotesNotTakenUp ?? 0} other
              quote(s) and {result.quoteRequestsNotTakenUp ?? 0} quote request(s) marked not taken up.
            </AlertDescription>
          </Box>
        </Alert>
      )}

      {rows.length === 0 ? (
        <Text fontSize="sm" color="gray.500">No quote requests yet.</Text>
      ) : (
        <Table size="sm">
          <Thead>
            <Tr>
              <Th w="1%" />
              <Th>Quote request</Th>
              <Th>Status</Th>
              <Th>Requested</Th>
              <Th>Response by</Th>
              <Th>Quotes</Th>
              <Th>Declined</Th>
            </Tr>
          </Thead>
          <Tbody>
            {groups.map((group) => {
              const lead = leadsByParty[group.partyId];
              const response = RESPONSES[lead?.responseCode];
              return (
                <Fragment key={group.partyId}>
                  <Tr bg="gray.100">
                    <Td colSpan={7} py={2}>
                      <HStack spacing={3} flexWrap="wrap">
                        <Text fontWeight="bold">{names[group.partyId] || "Loading…"}</Text>
                        <Text fontSize="xs" color="gray.500">
                          {group.rows.length} quote request{group.rows.length > 1 ? "s" : ""}
                        </Text>
                        {response && (
                          <HStack spacing={1} fontSize="xs" color="gray.600">
                            <Badge colorScheme={response.colour} textTransform="none">{response.label}</Badge>
                            <Text>{formatDate(lead.respondedDate)}</Text>
                          </HStack>
                        )}
                        {group.rows.length > 1 && !alreadyNominated && (
                          <Text fontSize="xs" color="gray.500">· nominate at most one of its quotes</Text>
                        )}
                      </HStack>
                      {lead?.responseNotes && (
                        <Text fontSize="xs" color="gray.600" whiteSpace="pre-wrap" noOfLines={3} mt={1}>{lead.responseNotes}</Text>
                      )}
                    </Td>
                  </Tr>
                  {group.rows.map((row, index) => {
                    const quotes = row.quotes || [];
                    const isOpen = expanded[row.quoteRequestId] ?? quotes.length > 0;
                    const request = requestsById[row.quoteRequestId];
                    const terms = readParticipation(request || row);
                    const sent = request?.receiver?.sentDate || request?.createdDate || row.sentDate || row.createdDate;
                    const notes = request?.notes ?? row.notes;
                    return (
                      <Fragment key={row.quoteRequestId}>
                        <Tr bg={row.status === "nominated" ? "green.50" : undefined}>
                          <Td>
                            {quotes.length > 0 && (
                              <IconButton
                                size="xs"
                                variant="ghost"
                                aria-label={isOpen ? "Hide quotes" : "Show quotes"}
                                icon={isOpen ? <ChevronDownIcon /> : <ChevronRightIcon />}
                                onClick={() => toggleRow(row.quoteRequestId)}
                              />
                            )}
                          </Td>
                          <Td>
                            <Text fontSize="sm">
                              Request {index + 1}
                              {sent && <Text as="span" color="gray.500"> · sent {formatDate(sent)}</Text>}
                            </Text>
                            {notes && <Text fontSize="xs" color="gray.600" whiteSpace="pre-wrap" noOfLines={3}>{notes}</Text>}
                          </Td>
                          <Td><PlacingStatusBadge status={row.status} /></Td>
                          <Td>
                            {(terms.requestedShare ?? row.requestedShare) != null ? `${terms.requestedShare ?? row.requestedShare}%` : "—"}
                            {formatShareRange(terms.minimumShare, terms.maximumShare) && (
                              <Text fontSize="xs" color="gray.500">{formatShareRange(terms.minimumShare, terms.maximumShare)}</Text>
                            )}
                          </Td>
                          <Td>
                            {formatDate(row.responseRequiredByDate)}
                            {row.overdue && <Badge ml={2} colorScheme="red">OVERDUE</Badge>}
                          </Td>
                          <Td>{quotes.length}</Td>
                          <Td>
                            {formatDate(row.declinedDate)}
                            {row.status === "declined" && (
                              <ReasonText color="red.600" code={row.reasonCode ?? row.receiver?.reasonCode} notes={row.declineReason} />
                            )}
                            {row.status === "ntu" && <ReasonText color="orange.700" prefix="NTU · " code={row.ntuReasonCode} notes={row.ntuNotes} />}
                          </Td>
                        </Tr>
                        {isOpen && quotes.length > 0 && (
                          <Tr>
                            <Td />
                            <Td colSpan={6} pt={0}>
                              <Table size="sm" variant="simple" bg="gray.50">
                                <Thead>
                                  <Tr>
                                    {!alreadyNominated && <Th>Nominate</Th>}
                                    <Th>Reference</Th>
                                    <Th>Stamp</Th>
                                    <Th>Share</Th>
                                    <Th>Range</Th>
                                    <Th>Currency</Th>
                                    <Th>Valid until</Th>
                                    <Th>Subjectivities</Th>
                                    <Th>Quote detail</Th>
                                    <Th>Stamped</Th>
                                  </Tr>
                                </Thead>
                                <Tbody>
                                  {quotes.map((quote) => (
                                    <Fragment key={quote.quoteId}>
                                      <Tr opacity={quote.expired ? 0.6 : 1}>
                                        {!alreadyNominated && (
                                          <Td>
                                            {/* Keyed by carrier: choosing this clears its other quotes */}
                                            <Checkbox
                                              isChecked={chosen[group.partyId] === quote.quoteId}
                                              isDisabled={!canNominate(row, quote)}
                                              onChange={() => chooseQuote(group.partyId, quote.quoteId)}
                                            />
                                          </Td>
                                        )}
                                        <Td>{quote.reference || "—"}</Td>
                                        <Td>{quote.stampReference || "—"}</Td>
                                        <Td fontWeight="semibold">{quote.quotedShare != null ? `${quote.quotedShare}%` : "—"}</Td>
                                        <Td>{formatShareRange(quote.minimumShare, quote.maximumShare)}</Td>
                                        <Td>{quote.currency || ""}</Td>
                                        <Td>
                                          {formatDate(quote.validUntilDate)}
                                          {quote.expired && <Badge ml={2} colorScheme="red">EXPIRED</Badge>}
                                        </Td>
                                        <Td maxW="260px" whiteSpace="pre-wrap">{quote.subjectivities || ""}</Td>
                                        <Td>
                                          {quote.notes ? (
                                            <Button size="xs" variant="link" colorScheme="blue" onClick={() => setOpenDetail((d) => ({ ...d, [quote.quoteId]: !d[quote.quoteId] }))}>
                                              {openDetail[quote.quoteId] ? "Hide detail" : "Show detail"}
                                            </Button>
                                          ) : (
                                            <Text color="gray.400">—</Text>
                                          )}
                                        </Td>
                                        <Td>
                                          {formatDate(quote.stampedDate)}
                                          {quote.status === "ntu" && (
                                            <ReasonText color="orange.700" prefix="NTU · " code={quote.ntuReasonCode} notes={quote.ntuNotes} />
                                          )}
                                        </Td>
                                      </Tr>
                                      {openDetail[quote.quoteId] && (
                                        <Tr>
                                          <Td colSpan={alreadyNominated ? 9 : 10} bg="white">
                                            <Text fontSize="xs" color="gray.500" mb={1}>Quote detail</Text>
                                            <Text fontSize="sm" whiteSpace="pre-wrap">{quote.notes}</Text>
                                          </Td>
                                        </Tr>
                                      )}
                                    </Fragment>
                                  ))}
                                </Tbody>
                              </Table>
                            </Td>
                          </Tr>
                        )}
                      </Fragment>
                    );
                  })}
                </Fragment>
              );
            })}
          </Tbody>
        </Table>
      )}

      {!alreadyNominated && rows.length > 0 && (
        <HStack mt={4}>
          <Button size="sm" colorScheme="green" onClick={confirmModal.onOpen} isLoading={nominating} isDisabled={Object.keys(chosen).length === 0}>
            Nominate {Object.keys(chosen).length || ""} lead(s)
          </Button>
          <Text fontSize="xs" color="gray.500">Whole contract only; one quote per carrier (across its requests); nomination happens once.</Text>
        </HStack>
      )}
      <Modal isOpen={confirmModal.isOpen} onClose={confirmModal.onClose} isCentered size="lg">
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>Nominate lead</ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            <Text fontSize="sm" mb={3}>
              Nominate <b>{leadNames}</b> as lead.
              {ntuNeeded
                ? ` ${quotesToNtu} other quote(s) and ${requestsToNtu} open quote request(s) will be marked not taken up (NTU).`
                : " Nothing else is outstanding, so nothing will be NTU'd."}
            </Text>
            {ntuNeeded && (
              <>
                <ReasonSelect label="Reason for NTU" isRequired value={ntuReasonCode} onChange={setNtuReasonCode} mb={3} />
                <FormControl isRequired={isOther(ntuReasonCode)}>
                  <FormLabel fontSize="xs">{isOther(ntuReasonCode) ? "NTU notes" : "NTU notes (optional)"}</FormLabel>
                  <Textarea size="sm" value={ntuNotes} onChange={(e) => setNtuNotes(e.target.value)} />
                </FormControl>
              </>
            )}
          </ModalBody>
          <ModalFooter>
            <Button variant="ghost" mr={3} onClick={confirmModal.onClose}>Cancel</Button>
            <Button colorScheme="green" onClick={nominate} isLoading={nominating}>Nominate</Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </Box>
  );
};

export default QuoteComparison;
